'use client'

import { useContext } from 'react'

import { cx } from '@/lib/utils'

import { LicenseMapFilterContext } from './license-map-filter-context'
import type { LicenseMapCategory } from './license-map-types'

export interface LicenseMapLegendItem {
  category: LicenseMapCategory
  label: string
  /** Already-localized count line, e.g. "11 jurisdictions". */
  count: string
}

interface LicenseMapLegendProps {
  items: LicenseMapLegendItem[]
  ariaLabel: string
}

// Legend swatches double as filters: pressing one off dims that category on
// the map and hides its rows in the table. State lives in LicenseMapInteractive
// (the context provider) so the server-rendered map and table react via CSS.
export const LicenseMapLegend = ({ items, ariaLabel }: LicenseMapLegendProps) => {
  const { off, toggle } = useContext(LicenseMapFilterContext)
  return (
    <div role="group" aria-label={ariaLabel} className="mb-3.5 flex flex-wrap gap-2">
      {items.map((item) => {
        const pressed = !off.includes(item.category)
        return (
          <button
            key={item.category}
            type="button"
            aria-pressed={pressed}
            data-tracking="ignore"
            onClick={() => toggle(item.category)}
            className={cx(
              'border-navy-20 hover:border-navy-40 focus-visible:outline-lapis text-navy flex cursor-pointer items-center gap-2 rounded-full border bg-transparent py-1.5 pr-3.5 pl-2.5 text-left transition-[opacity,border-color] focus-visible:outline-2 focus-visible:outline-offset-2',
              !pressed && 'opacity-40'
            )}
          >
            <span className="swatch" data-cat={item.category} aria-hidden="true" />
            <span className="flex flex-col leading-tight">
              <strong className="text-[13px] font-semibold">{item.label}</strong>
              <span className="text-navy-60 text-[11.5px] tabular-nums">{item.count}</span>
            </span>
          </button>
        )
      })}
    </div>
  )
}
